// src/components/overtime/OvertimeRequestModal.js
import React from "react";
import { useAuth } from "../navigation/AuthProvider";

const two = (n) => String(n).padStart(2, "0");
const formatSubmitted = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const ymd = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
    const hm = `${two(d.getHours())}:${two(d.getMinutes())}`;
    return `${ymd} • ${hm}`; // tillbaka till dot-format
  } catch {
    return "—";
  }
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    width: "100%",
    maxWidth: 600,
    background: "white",
    borderRadius: 12,
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    padding: 20,
    maxHeight: "90vh",
    overflowY: "auto",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  title: { margin: 0, fontWeight: 700, fontSize: 18, color: "#0f172a" },
  closeBtn: {
    background: "transparent",
    border: "1px solid #e5e7eb",
    color: "#0f172a",
    borderRadius: 8,
    fontSize: 14,
    padding: "6px 10px",
    cursor: "pointer",
  },
  row: { display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, padding: "10px 0", borderBottom: "1px solid #f1f5f9" },
  label: { fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 },
  value: { color: "#0f172a", fontSize: 14 },
  valueStack: { color: "#0f172a", fontSize: 14, display: "flex", flexDirection: "column" },
  valueProject: { fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  valueSub: { fontSize: 13, color: "#64748b", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  badge: (status) => {
    const base = { display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 };
    if (status === "approved") return { ...base, background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" };
    if (status === "rejected") return { ...base, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" };
    return { ...base, background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" };
  },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 },
  approve: { background: "#10b981", color: "white", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700 },
  reject: { background: "#ef4444", color: "white", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700 },
};

export default function OvertimeRequestModal({ open, request, onClose, canDecide, onApprove, onReject, isReadOnly }) {
  const { projects, draftSchedule } = useAuth();
  if (!open) return null;
  const r = request || {};
  const line = (label, val) => (
    <div style={styles.row}>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>{val || "—"}</div>
    </div>
  );

  const shiftLine = () => {
    const datePart = r.shiftDate && r.shiftStart && r.shiftEnd ? `${r.shiftDate} • ${r.shiftStart}-${r.shiftEnd}` : "—";
    let project = "—";
    try {
      const shift = (draftSchedule || []).find((s) => String(s._id) === String(r.shift));
      const projId = shift?.project || shift?.projectId;
      const p = (projects || []).find((pp) => String(pp._id) === String(projId));
      project = p?.name || p?.title || "—";
    } catch {}
    return (
      <div style={styles.row}>
        <div style={styles.label}>Skift</div>
        <div style={styles.valueStack}>
          <span style={styles.valueProject}>{project}</span>
          <span style={styles.valueSub}>{datePart}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Ansökan om övertid</h3>
          <button type="button" onClick={onClose} style={styles.closeBtn}>Stäng</button>
        </div>

        {line("Sökande", r.employeeName || r.employeeEmail)}
        {/* Ny rad: Inskickat */}
        {line("Inskickad", formatSubmitted(r.createdAt))}
        {shiftLine()}
        {line("Övertid", `${(r.from || "").padStart(5,"0")} – ${(r.to || "").padStart(5,"0")}`)}

        <div style={styles.row}>
          <div style={styles.label}>Status</div>
          <div><span style={styles.badge(r.status)}>{r.status === "approved" ? "Godkänd" : r.status === "rejected" ? "Nekad" : "Väntar"}</span></div>
        </div>

        {line("Orsak", r.reason)}
        {r.decisionNote ? line("Återkoppling", r.decisionNote) : null}

        {(canDecide && r.status === "pending" && !isReadOnly) && (
          <div style={styles.actions}>
            <button onClick={onReject} style={styles.reject}>Neka</button>
            <button onClick={onApprove} style={styles.approve}>Godkänn</button>
          </div>
        )}
      </div>
    </div>
  );
}
